import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type HTMLAttributes } from "react";

import {
  getAudiencesQueryOptions,
  useGetAudienceMemberCount,
  type AudienceListItem,
} from "@/api/audiences";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  CheckBoxIcon,
  CheckBoxOutlineBlankIcon,
  PeopleOutlineIcon,
} from "@/icons";
import { formatNumber } from "@/utils/metrics";

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_PAGE = 1;
const SEARCH_PAGE_SIZE = 20;

export interface SelectAudienceOption {
  id: string;
  name: string;
  key: string;
  count?: number;
}

interface SelectAudienceFieldProps {
  siteId: string;
  value: SelectAudienceOption[];
  onChange: (value: SelectAudienceOption[]) => void;
  disabled?: boolean;
  /**
   * Audience ids that are unavailable for selection here because they are
   * already chosen in the complementary list (target vs. exclusion). They are
   * hidden from the dropdown but never removed from `value`.
   */
  unavailableIds?: string[];
  /** Placeholder shown when nothing is selected. */
  placeholder?: string;
  /** Accessible label for the input. */
  ariaLabel?: string;
}

const toAudienceOption = (
  audience: AudienceListItem,
): SelectAudienceOption => ({
  id: audience.id,
  name: audience.name,
  key: `audience:${audience.id}`,
  count: audience.count,
});

export function SelectAudienceField({
  siteId,
  value,
  onChange,
  disabled = false,
  unavailableIds,
  placeholder,
  ariaLabel = "Select audience",
}: SelectAudienceFieldProps) {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  const audiencesQuery = useQuery(
    getAudiencesQueryOptions(siteId, {
      page: SEARCH_PAGE,
      pageSize: SEARCH_PAGE_SIZE,
      search: debouncedSearch || undefined,
    }),
  );

  // Authoritative per-audience counts for the selected chips (covers audiences
  // not present in the current search page, e.g. pre-selected ones on edit).
  const selectedIds = value.map((option) => option.id);
  const chipCountsQuery = useGetAudienceMemberCount(siteId, selectedIds);
  const chipCounts = chipCountsQuery.data?.audiences;

  const hasError = audiencesQuery.isError;

  const unavailable = useMemo(
    () => new Set(unavailableIds ?? []),
    [unavailableIds],
  );

  const options = useMemo(() => {
    const merged = new Map<string, SelectAudienceOption>();

    for (const selected of value) {
      merged.set(selected.key, selected);
    }

    for (const audience of audiencesQuery.data?.data ?? []) {
      // Hide audiences already chosen in the complementary list, but keep any
      // that are currently selected here (present in `value`) to avoid dropping
      // an existing chip.
      if (unavailable.has(audience.id)) continue;
      const option = toAudienceOption(audience);
      merged.set(option.key, option);
    }

    return Array.from(merged.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [audiencesQuery.data?.data, value, unavailable]);

  return (
    <Autocomplete<SelectAudienceOption, true, false, false>
      multiple
      disableCloseOnSelect
      disabled={disabled}
      options={options}
      value={value}
      inputValue={searchInput}
      loading={audiencesQuery.isFetching}
      loadingText="Loading audiences..."
      noOptionsText={
        debouncedSearch ? "No audiences found" : "No audiences available"
      }
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selectedValue) =>
        option.key === selectedValue.key
      }
      filterOptions={(availableOptions) => availableOptions}
      onChange={(_, nextValue) => {
        onChange(nextValue);
      }}
      onInputChange={(_, nextInputValue) => {
        setSearchInput(nextInputValue);
      }}
      renderInput={(params) => (
        <>
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              "aria-label": ariaLabel,
            }}
            error={hasError}
            placeholder={
              value.length === 0
                ? (placeholder ??
                  "e.g. Instagram Leads, High Value Customers...")
                : ""
            }
            sx={{
              backgroundColor: "common.white",
            }}
          />
          {hasError && (
            <Box
              sx={(theme) => ({
                backgroundColor: theme.palette.background["paper-elevation-1"],
                p: 3,
                display: "flex",
                alignItems: "center",
              })}
            >
              <Typography variant="body2" color="text.secondary">
                Unable to load audiences. Please refresh and try again.
              </Typography>
              <Button
                size="small"
                variant="text"
                data-testid="select-audience-retry"
                disabled={disabled || audiencesQuery.isFetching}
                onClick={() => {
                  void audiencesQuery.refetch();
                }}
              >
                Retry
              </Button>
            </Box>
          )}
        </>
      )}
      renderOption={(
        props: HTMLAttributes<HTMLLIElement> & { key: string },
        option,
        { selected },
      ) => {
        const { key, ...rest } = props;

        return (
          <li key={key} {...rest}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ width: "100%" }}
            >
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                checked={selected}
                sx={{ pointerEvents: "none" }}
              />
              <PeopleOutlineIcon fontSize="small" />
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ minWidth: 0, flex: 1 }}
              >
                <Typography variant="body1" noWrap>
                  {option.name}
                </Typography>
                {option.count != null && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid={`audience-count-${option.id}`}
                  >
                    ({formatNumber(option.count)})
                  </Typography>
                )}
              </Stack>
            </Stack>
          </li>
        );
      }}
      renderTags={(selectedOptions, getTagProps) =>
        selectedOptions.map((option, index) => {
          const { key, ...chipProps } = getTagProps({ index });
          const chipCount = chipCounts?.[option.id] ?? option.count;
          const chipLabel =
            chipCount != null
              ? `${option.name} (${formatNumber(chipCount)})`
              : option.name;

          return (
            <Chip
              {...chipProps}
              key={key}
              data-testid={`selected-chip-${option.key}`}
              icon={
                <PeopleOutlineIcon
                  fontSize="small"
                  data-testid="audience-chip-icon"
                />
              }
              label={chipLabel}
              size="small"
              sx={{
                "& .MuiChip-label": { color: "common.white" },
                "& .MuiChip-icon": { color: "common.white" },
              }}
            />
          );
        })
      }
      slotProps={{
        paper: {
          sx: { bgcolor: "background.paper-elevation-2" },
        },
        listbox: {
          sx: {
            maxHeight: 320,
            bgcolor: "background.paper-elevation-2",
          },
        },
      }}
    />
  );
}
